const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 
  (process.env.NODE_ENV === 'development' 
    ? 'http://localhost:4000' 
    : 'https://api.lifelikeagents.com');  // Update to use api subdomain

if (process.env.NODE_ENV !== 'development' && !process.env.REACT_APP_BACKEND_URL) {
  console.warn('Using default production API URL because REACT_APP_BACKEND_URL is not set');
}

class ApiClient {
  constructor() {
    // Remove trailing slashes but don't add /api here
    this.baseUrl = API_BASE_URL.replace(/\/+$/, '');
    this.token = null;
    
    if (process.env.NODE_ENV === 'development') {
      // console.log('API Base URL:', this.baseUrl);
    }
  }

  setToken(token) {
    this.token = token;
  }

  async request(endpoint, options = {}) {
    // Ensure endpoint starts with /api and clean up any double slashes
    const cleanEndpoint = endpoint.replace(/^\/+/, '').replace(/\/+/g, '/');
    const url = `${this.baseUrl}/${cleanEndpoint}`;
    
    // Basic headers that should work in all environments
    const headers = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      ...options.headers
    };

    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }

    // Fetch options with credentials and CORS mode
    const fetchOptions = {
      ...options,
      headers,
      credentials: 'include',
      mode: 'cors'
    };

    try {
      if (process.env.NODE_ENV === 'development') {
        console.log('Making request to:', url);
        console.log('With options:', {
          ...fetchOptions,
          headers: { ...fetchOptions.headers }
        });
      }

      const response = await fetch(url, fetchOptions);
      
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        console.error('API Error Response:', {
          status: response.status,
          statusText: response.statusText,
          data
        });
        
        // Enhanced 403 handling with captcha detection
        if (response.status === 403) {
          // Log extra details for 403 responses to help diagnose issues
          console.log('Received 403 response with data:', data);
          
          // Check for captcha requirement in various possible locations
          const requiresCaptcha = 
            data.requireCaptcha === true || 
            (data.status === 'error' && data.message?.includes('Captcha') && data.requireCaptcha) ||
            data.message?.toLowerCase().includes('captcha verification required');
            
          if (requiresCaptcha) {
            console.log('Detected captcha requirement in 403 response');
            const err = new Error(data.message || 'Captcha verification required');
            err.status = response.status;
            err.statusCode = 403; // Additional property for more robust checking
            err.requireCaptcha = true; // Add directly to error object
            err.data = data; // Store the full data directly on the error
            err.response = { data, status: response.status };
            throw err;
          }
        }
        
        const message = data.message || data.error || `HTTP error! status: ${response.status}`;
        const err = new Error(message);
        err.status = response.status;
        err.statusCode = response.status; // Additional property for more robust checking
        err.response = { data, status: response.status };
        err.data = data; // Store the full data directly on the error
        
        // Copy any important flags from data to the error object to make them easier to access
        if (data.requireCaptcha) {
          err.requireCaptcha = data.requireCaptcha;
        }
        
        if (data.throttled) {
          err.throttled = data.throttled;
        }
        
        if (data.timeRemaining) {
          err.timeRemaining = data.timeRemaining;
        }
        
        throw err;
      }

      // Handle blob responses (like for downloadCallRecording)
      if (options.responseType === 'blob') {
        return response.blob();
      }

      const data = await response.json().catch(() => ({}));
      
      // Check for captcha requirement in successful responses too
      if (data.requireCaptcha) {
        console.log('Detected captcha requirement in successful response:', data);
      }
      
      return data;
    } catch (error) {
      console.error('API Request Error:', error);
      if (error instanceof TypeError && error.message === 'Failed to fetch') {
        throw new Error('Network error: Unable to connect to the server');
      }
      throw error;
    }
  }

  // Auth endpoints
  async login(email, password) {
    const response = await this.request('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });
    this.setToken(response.token);
    return response;
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/change-password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword })
    });
  }

  logout() {
    this.setToken(null);
  }

  // User management endpoints (admin only)
  async createUser(userData) {
    const response = await this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify({
        email: userData.email,
        password: userData.password,
        backend_api_key: userData.backend_api_key
      })
    });
    return response;
  }

  async updateUserBackendApiKey(userId, backendApiKey) {
    const response = await this.request(`/admin/users/${userId}/backend-api-key`, {
      method: 'PUT',
      body: JSON.stringify({
        backend_api_key: backendApiKey
      })
    });
    return response;
  }

  async getUsers() {
    return this.request('/admin/users');
  }

  async deactivateUser(userId) {
    return this.request(`/admin/users/${userId}`, {
      method: 'DELETE'
    });
  }

  // Voice / telephony provider endpoints
  async sendCall(callData, options = {}) {
    const { encryptedKey } = options;
    const headers = {};
    if (encryptedKey) {
      headers.encrypted_key = encryptedKey;
    }
    return this.request('/send-call-simple', {
      method: 'POST',
      headers,
      body: JSON.stringify(callData)
    });
  }

  async getCalls() {
    return this.request('/calls');
  }

  async getCallsWithFilters(params) {

    const response = await this.request('/calls');
    //filter with params
    const filteredCalls = response.filter(call => {
      return Object.keys(params).every(key => call[key] === params[key]);
    });
    return filteredCalls;
  }

  async getTotalCallTime() {
    return this.request('/total-call-time');
  }

  async getCallDetails(callId) {
    return this.request(`/calls/${callId}`);
  }

  async getCallRecording(callId) {
    return this.request(`/calls/${callId}/recording`);
  }

  async downloadCallRecording(callId) {
    return this.request(`/calls/${callId}/recording/download`, {
      method: 'GET',
      responseType: 'blob'
    });
  }

  async stopAllActiveCalls() {
    return this.request('/calls/active/stop', {
      method: 'POST'
    });
  }

  async stopCall(callId) {
    return this.request(`/calls/${callId}/stop`, {
      method: 'POST'
    });
  }

  async analyzeCall(callId, data) {
    return this.request(`/calls/${callId}/analyze`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async analyzeCallEmotions(callId) {
    return this.request('/intelligence/emotions', {
      method: 'POST',
      body: JSON.stringify({ callId })
    });
  }

  async getdialogFlows() {
    return this.request('/dialogFlows');
  }

  async getdialogFlow(dialogFlowId) {
    return this.request(`/dialogFlow/${dialogFlowId}`);
  }

  async updateDialogFlow(dialogFlowId, data) {
    return this.request(`/dialogFlow/${dialogFlowId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async createdialogFlowChat(data) {
    return this.request('/dialogFlow/chat/create', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async senddialogFlowMessage(chatId, message) {
    return this.request(`/dialogFlow/chat/${chatId}`, {
      method: 'POST',
      body: JSON.stringify({ message })
    });
  }

  async getdialogFlowChat(chatId) {
    return this.request(`/dialogFlow/chat/${chatId}`);
  }

  async createDialogFlow(data) {
    return this.request('/dialogFlows/create', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteDialogFlow(dialogFlowId) {
    return this.request(`/dialogFlow/${dialogFlowId}`, {
      method: 'DELETE',
    });
  }

  async renameDialogFlow(dialogFlowId, newName) {
    return this.request(`/dialogFlow/${dialogFlowId}/rename`, {
      method: 'POST',
      body: JSON.stringify({ name: newName }),
    });
  }

  async getDialogFlowVersions(dialogFlowId) {
    return this.request(`/dialogFlow/${dialogFlowId}/versions`);
  }

  async promoteDialogFlowVersion(dialogFlowId, versionId, environment = 'production') {
    return this.request(`/dialogFlow/${dialogFlowId}/publish`, {
      method: 'POST',
      body: JSON.stringify({
        version_id: versionId,
        environment
      })
    });
  }

  async getDialogFlowVersion(dialogFlowId, versionId) {
    return this.request(`/dialogFlow/${dialogFlowId}/version/${versionId}`);
  }

  async createDialogFlowVersion(dialogFlowId, data) {
    return this.request(`/dialogFlow/${dialogFlowId}/version`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateDialogFlowVersion(dialogFlowId, versionNumber, data) {
    return this.request(`/dialogFlow/${dialogFlowId}/version/${versionNumber}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteDialogFlowVersion(dialogFlowId, versionId) {
    return this.request(`/dialogFlow/${dialogFlowId}/version/${versionId}`, {
      method: 'DELETE'
    });
  }

  // Knowledge Base endpoints
  async createKnowledgeBase(data) {
    return this.request('/knowledgebases', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async uploadKnowledgeBaseMedia(formData) {
    // Special handling for file upload
    const url = `${this.baseUrl}/knowledgebases/upload-media`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-API-Key': this.token,
        'Authorization': this.token
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Upload failed');
    }

    return response.json();
  }

  async uploadKnowledgeBaseText(formData) {
    // Special handling for file upload
    const url = `${this.baseUrl}/knowledgebases/upload`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-API-Key': this.token,
        'Authorization': this.token
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Upload failed');
    }

    return response.json();
  }

  async getKnowledgeBases() {
    return this.request('/knowledgebases');
  }

  async getKnowledgeBaseDetails(vectorId) {
    return this.request(`/knowledgebases/${vectorId}`);
  }

  async deleteKnowledgeBase(vectorId) {
    return this.request(`/knowledgebases/${vectorId}`, {
      method: 'DELETE'
    });
  }

  async updateKnowledgeBase(vectorId, data) {
    return this.request(`/knowledgebases/${vectorId}`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // Credit system endpoints
  async getCreditBalance() {
    const response = await this.request('/credits/balance');
    if (typeof response.credits !== 'number') {
      // Convert to number and ensure 2 decimal places
      return { credits: Number(parseFloat(response.credits || 0).toFixed(2)) };
    }
    return response;
  }

  async getCreditPackages() {
    return this.request('/credits/packages');
  }

  async getCreditTransactions() {
    return this.request('/credits/transactions');
  }

  // Admin credit management endpoints
  async getAdminCreditSummary() {
    return this.request('/admin/credits/summary');
  }

  async getUserTransactions(userId) {
    return this.request(`/admin/credits/transactions/${userId}`);
  }

  async addCredits(userId, amount, description) {
    return this.request('/admin/credits/add', {
      method: 'POST',
      body: JSON.stringify({ userId, amount, description })
    });
  }

  // Prompt management endpoints
  async getPrompts() {
    return this.request('/prompts');
  }

  async getPromptDetails(promptId) {
    return this.request(`/prompts/${promptId}`);
  }

  async createPrompt(data) {
    return this.request('/prompts', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updatePrompt(promptId, data) {
    return this.request(`/prompts/${promptId}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deletePrompt(promptId) {
    return this.request(`/prompts/${promptId}`, {
      method: 'DELETE'
    });
  }

  // Voice management endpoints
  async cloneVoice(name, audioSamples, description = null) {
    const formData = new FormData();
    formData.append('name', name);
    if (description) {
      formData.append('description', description);
    }
    
    // Handle multiple audio samples
    if (Array.isArray(audioSamples)) {
      audioSamples.forEach((file) => {
        formData.append('audio_samples', file);
      });
    } else {
      formData.append('audio_samples', audioSamples);
    }

    // Special handling for file upload - don't set Content-Type header
    const url = `${this.baseUrl}/voices`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'X-API-Key': this.token,
        'Authorization': this.token
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.details || error.message || 'Voice cloning failed');
    }

    return response.json();
  }

  async renameVoice(voiceId, newName, description = null) {
    return this.request(`/voices/${voiceId}/rename`, {
      method: 'PATCH',
      body: JSON.stringify({ 
        name: newName,
        description: description 
      })
    });
  }

  async deleteVoice(voiceId) {
    return this.request(`/voices/${voiceId}`, {
      method: 'DELETE'
    });
  }

  async getVoices() {
    return this.request('/voices');
  }

  async getVoiceDetails(voiceId) {
    return this.request(`/voices/${voiceId}`);
  }

  async addVoiceToFavorites(voiceId, name) {
    return this.request(`/voices/${voiceId}/favorite`, {
      method: 'POST',
      body: JSON.stringify({ name })
    });
  }

  async removeVoiceFromFavorites(voiceId) {
    return this.request(`/voices/${voiceId}/favorite`, {
      method: 'DELETE'
    });
  }

  async publishVoice(voiceId) {
    return this.request(`/voices/${voiceId}/publish`, {
      method: 'POST'
    });
  }

  async generateVoiceSample(voiceId, text, voiceSettings = null, language = null) {
    // Must use request() like other endpoints: correct /api prefix on Vercel, Bearer auth, credentials.
    return this.request(`/voices/${voiceId}/sample`, {
      method: 'POST',
      body: JSON.stringify({
        text,
        ...(voiceSettings && { voice_settings: voiceSettings }),
        ...(language && { language })
      }),
      responseType: 'blob',
      headers: {
        Accept: 'audio/*, application/json'
      }
    });
  }

  // Phone Number Management endpoints
  async purchaseNumber(data) {
    return this.request('/numbers/purchase', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateNumber(phoneNumber, data) {
    return this.request(`/numbers/${phoneNumber}/update`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getNumbers(encryptedKey = null) {
    const headers = {};
    if (encryptedKey) {
      headers.encrypted_key = encryptedKey;
    }
    return this.request('/numbers', { headers });
  }

  async getNumberDetails(phoneNumber) {
    return this.request(`/numbers/${phoneNumber}`);
  }

  // Custom Twilio Account methods
  async createEncryptedKey(accountSid, authToken) {
    return this.request('/accounts', {
      method: 'POST',
      body: JSON.stringify({
        account_sid: accountSid,
        auth_token: authToken
      })
    });
  }

  async deleteEncryptedKey(encryptedKey) {
    return this.request('/accounts/delete', {
      method: 'POST',
      headers: {
        encrypted_key: encryptedKey
      }
    });
  }

  async uploadInboundNumbers(encryptedKey, numbers) {
    return this.request('/inbound/insert', {
      method: 'POST',
      headers: {
        encrypted_key: encryptedKey
      },
      body: JSON.stringify({ numbers })
    });
  }

  async deleteInboundNumber(encryptedKey, phoneNumber) {
    return this.request(`/inbound/${phoneNumber}/delete`, {
      method: 'POST',
      headers: {
        encrypted_key: encryptedKey
      }
    });
  }

  createEventStream(callId) {
    const url = new URL(`${this.baseUrl}/event_stream/${callId}`);
    url.searchParams.append('apiKey', this.token);
    const eventSource = new EventSource(url.toString());
    return eventSource;
  }

  // Batch endpoints
  async createBatch(data) {
    const { base_prompt, call_data, from, label, campaign_id, test_mode, ...otherParams } = data;
    return this.request('/batches', {
      method: 'POST',
      body: JSON.stringify({
        base_prompt,
        call_data,
        ...(from && { from }),
        ...(label && { label }),
        ...(campaign_id && { campaign_id }),
        ...(test_mode !== undefined && { test_mode }),
        ...otherParams
      })
    });
  }

  async analyzeBatch(batchId, data) {
    const { goal, questions } = data;
    return this.request(`/batches/${batchId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ goal, questions })
    });
  }

  async stopBatch(batchId) {
    return this.request(`/batches/${batchId}/stop`, {
      method: 'POST'
    });
  }

  async getBatches(params = {}) {
    const { campaign_id, from, to, limit, ascending } = params;
    const queryParams = new URLSearchParams();
    if (campaign_id) queryParams.append('campaign_id', campaign_id);
    if (from) queryParams.append('from', from);
    if (to) queryParams.append('to', to);
    if (limit) queryParams.append('limit', limit);
    if (ascending) queryParams.append('ascending', ascending);

    return this.request(`/batches${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
  }

  async getBatchDetails(batchId, params = {}) {
    const { include_calls, include_transcripts, include_analysis } = params;
    const queryParams = new URLSearchParams();
    if (include_calls !== undefined) queryParams.append('include_calls', include_calls);
    if (include_transcripts !== undefined) queryParams.append('include_transcripts', include_transcripts);
    if (include_analysis !== undefined) queryParams.append('include_analysis', include_analysis);

    return this.request(`/batches/${batchId}${queryParams.toString() ? '?' + queryParams.toString() : ''}`);
  }

  async getBatchAnalysis(batchId) {
    return this.request(`/batches/${batchId}/analysis`);
  }

  // Web Agents endpoints
  async createWebAgent(data) {
    const {
      name,
      prompt,
      voice,
      webhook,
      analysis_schema,
      metadata,
      pathway_id,
      language,
      model,
      first_sentence,
      tools,
      dynamic_data,
      interruption_threshold,
      keywords,
      max_duration
    } = data;

    return this.request('/agents', {
      method: 'POST',
      body: JSON.stringify({
        name,
        prompt,
        ...(voice && { voice }),
        ...(webhook && { webhook }),
        ...(analysis_schema && { analysis_schema }),
        ...(metadata && { metadata }),
        ...(pathway_id && { pathway_id }),
        ...(language && { language }),
        ...(model && { model }),
        ...(first_sentence && { first_sentence }),
        ...(tools && { tools }),
        ...(dynamic_data && { dynamic_data }),
        ...(interruption_threshold && { interruption_threshold }),
        ...(keywords && { keywords }),
        ...(max_duration && { max_duration })
      })
    });
  }

  async updateWebAgent(agentId, data) {
    const {
      name,
      prompt,
      voice,
      webhook,
      analysis_schema,
      metadata,
      pathway_id,
      language,
      model,
      first_sentence,
      tools,
      dynamic_data,
      interruption_threshold,
      max_duration
    } = data;

    // Send all data including name to our backend endpoint
    // The backend will handle extracting name before sending to the voice provider
    return this.request(`/agents/${agentId}`, {
      method: 'POST',
      body: JSON.stringify({
        name,
        prompt,
        ...(voice && { voice }),
        ...(webhook && { webhook }),
        ...(analysis_schema && { analysis_schema }),
        ...(metadata && { metadata }),
        ...(pathway_id && { pathway_id }),
        ...(language && { language }),
        ...(model && { model }),
        ...(first_sentence && { first_sentence }),
        ...(tools && { tools }),
        ...(dynamic_data && { dynamic_data }),
        ...(interruption_threshold && { interruption_threshold }),
        ...(max_duration && { max_duration })
      })
    });
  }

  async authorizeWebAgentCall(agentId) {
    return this.request(`/agents/${agentId}/authorize`, {
      method: 'POST'
    });
  }

  async deleteWebAgent(agentId) {
    return this.request(`/agents/${agentId}/delete`, {
      method: 'POST'
    });
  }

  async getWebAgents() {
    return this.request('/agents');
  }

  // Add new methods for public status management
  async getAgentPublicStatus(agentId) {
    const response = await this.request(`/agents/${agentId}/public-status`);
    return { isPublic: Boolean(response.isPublic) };
  }

  async setAgentPublicStatus(agentId, isPublic, name = null) {
    return this.request(`/agents/${agentId}/public-status`, {
      method: 'POST',
      body: JSON.stringify({ 
        isPublic: Boolean(isPublic),
        name
      })
    });
  }

  async authorizePublicAgent(agentId, captchaToken = null) {
    console.log('API authorizePublicAgent called with:', { 
      agentId, 
      hasCaptchaToken: !!captchaToken 
    });
    
    const body = captchaToken ? JSON.stringify({ captchaToken }) : '{}';
    console.log('Request body:', body);
    
    try {
      const response = await this.request(`/authorize-public-agent/${agentId}`, {
        method: 'POST',
        body
      });
      
      // Check if the response indicates captcha is required 
      // This is a direct success response but containing requireCaptcha flag
      if (response.requireCaptcha === true) {
        console.log('Detected requireCaptcha in successful response, converting to error');
        
        // Create and throw an error that matches our error structure for 403/captcha
        const err = new Error(response.message || 'Captcha verification required');
        err.status = 403;
        err.statusCode = 403;
        err.requireCaptcha = true;
        err.response = { data: response };
        err.data = response;
        throw err;
      }
      
      return response;
    } catch (error) {
      // Re-throw errors from the request function
      console.error('Error in authorizePublicAgent:', error);
      
      // Ensure the error has the requireCaptcha flag if it's in the response
      if (error.response?.data?.requireCaptcha || error.data?.requireCaptcha) {
        error.requireCaptcha = true;
      }
      
      throw error;
    }
  }

  // Blog post endpoints
  async getBlogPosts(published) {
    const query = published !== undefined ? `?published=${published}` : '';
    return this.request(`/blog-posts${query}`);
  }

  async getBlogPost(slug) {
    return this.request(`/blog-posts/${slug}`);
  }

  async uploadBlogImage(file) {
    const formData = new FormData();
    formData.append('image', file);

    const url = `${this.baseUrl}/blog-posts/upload-image`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || error.message || 'Failed to upload image');
    }

    return response.json();
  }

  async createBlogPost(data) {
    return this.request('/blog-posts', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateBlogPost(id, data) {
    return this.request(`/blog-posts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  async deleteBlogPost(id) {
    return this.request(`/blog-posts/${id}`, {
      method: 'DELETE'
    });
  }

  // Newsletter endpoints
  async subscribeToNewsletter(email) {
    return this.request('/newsletter/subscribe', {
      method: 'POST',
      body: JSON.stringify({ email })
    });
  }

  // Demo verification endpoint
  async verifyDemo(id, token) {
    // For verification we handle it differently from other API requests
    // because we need to return a URL that can be used for browser redirection
    
    // Don't use /api prefix for the verification route
    // In development, go directly to the backend server
    const redirectUrl = `${this.baseUrl}/verify-demo/${id}/${token}`;     // Use API URL in production
    
    console.log(`Verifying demo request: ${id} with token, redirecting to: ${redirectUrl}`);
    
    return { redirectUrl };
  }

  // Custom Tools endpoints
  async createCustomTool(data) {
    return this.request('/tools', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateCustomTool(toolId, data) {
    return this.request(`/tools/${toolId}`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async deleteCustomTool(toolId) {
    return this.request(`/tools/${toolId}`, {
      method: 'DELETE'
    });
  }

  async getCustomTools() {
    return this.request('/tools');
  }

  async getCustomToolDetails(toolId) {
    return this.request(`/tools/${toolId}`);
  }
}

export const api = new ApiClient();
export default api; 
